import {
  Alert,
  Box,
  Button,
  Card,
  CustomTheme,
  Fade,
  FormControl,
  FormLabel,
  Link,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import React, { ChangeEvent, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthProvider';
import {
  atLeast,
  isNotEmpty,
  isValidEmailAddress,
} from './../../helpers/formHelpers';
import { useTranslation } from 'react-i18next';

export const Login: React.FC = (): JSX.Element => {
  const { handleLogin } = useAuth();
  const { t } = useTranslation();

  const theme = useTheme<CustomTheme>();

  const passwordInputRef = useRef<HTMLInputElement>(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const [loginFailed, setLoginFailed] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = useState('');

  const hideLoginError = (): void => {
    setLoginFailed(false);
  };

  const showLoginError = (): void => {
    setLoginFailed(true);
  };

  const onForgotPasswordClick = (): void => {};

  const clearFormErrors = (): void => {
    setEmailError(false);
    setEmailErrorMessage('');

    setPasswordError(false);
    setPasswordErrorMessage('');
  };

  const validateInputs = (): void => {
    clearFormErrors();

    if (!isNotEmpty(email)) {
      setEmailError(true);
      setEmailErrorMessage(t('errors.required'));
    } else if (!isValidEmailAddress(email)) {
      setEmailError(true);
      setEmailErrorMessage(t('errors.invalidEmail'));
    }

    if (!isNotEmpty(password)) {
      setPasswordError(true);
      setPasswordErrorMessage(t('errors.required'));
    } else if (!atLeast(password, 5)) {
      setPasswordError(true);
      setPasswordErrorMessage(
        t('errors.atLeast', { field: 'password', min: 5 })
      );
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    hideLoginError();
    setIsLoggingIn(true);

    if (emailError || passwordError) {
      setIsLoggingIn(false);

      return;
    }

    const result = await handleLogin(email, password);

    if (!result) {
      setPassword('');
      passwordInputRef.current?.focus();
      showLoginError();
    }

    setIsLoggingIn(false);
  };

  return (
    <Stack
      direction="column"
      justifyContent="space-between"
      sx={{
        height: 'calc((1 - var(--template-frame-height, 0)) * 100dvh)',
        minHeight: '100%',
        minWidth: theme.breakpoints.values.xs,
        [theme.breakpoints.down('mobile')]: {
          display: 'block',
          pt: '20px',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignSelf: 'center',
          width: '100%',
          margin: 'auto',
          maxWidth: '450px',
        }}
      >
        <Box
          component="img"
          src="/logos/logo-canazados.png"
          sx={{
            width: '200px',
            mx: 'auto',
            mb: '20px',
          }}
        />

        <Card
          variant="outlined"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            p: '15px',
            gap: '10px',
            boxShadow:
              'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
          }}
        >
          <Typography
            component="h1"
            variant="h4"
            sx={{
              width: '100%',
              fontSize: 'clamp(2rem, 10vw, 2.15rem)',
            }}
          >
            {t('forms:login.title')}
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              gap: 2,
            }}
          >
            <Fade in={loginFailed} unmountOnExit>
              <Alert severity="error" variant="filled" onClose={hideLoginError}>
                {t('forms:login.errors.invalidLoginAttempt')}
              </Alert>
            </Fade>

            <FormControl>
              <FormLabel htmlFor="email" error={emailError}>
                {t('forms:login.labels.email')}
              </FormLabel>
              <TextField
                error={emailError}
                helperText={emailErrorMessage}
                id="email"
                type="email"
                name="email"
                placeholder={t('forms:login.placeholders.email')}
                autoComplete="email"
                autoFocus
                required
                fullWidth
                value={email}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setEmail(event.target.value)
                }
                variant="outlined"
                color={emailError ? 'error' : 'primary'}
              />
            </FormControl>
            <FormControl>
              <FormLabel htmlFor="password" error={passwordError}>
                {t('forms:login.labels.password')}
              </FormLabel>
              <TextField
                error={passwordError}
                helperText={passwordErrorMessage}
                name="password"
                inputRef={passwordInputRef}
                placeholder={t('forms:login.placeholders.password')}
                type="password"
                id="password"
                autoComplete="current-password"
                required
                fullWidth
                value={password}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setPassword(event.target.value)
                }
                variant="outlined"
                color={passwordError ? 'error' : 'primary'}
              />
            </FormControl>
            {/* <FormControlLabel
              control={<Checkbox value="remember" color="primary" />}
              label="Remember me"
            /> */}
            {/* <ForgotPassword open={open} handleClose={handleClose} /> */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              onClick={validateInputs}
              disabled={isLoggingIn}
            >
              {t('forms:login.buttons.signIn')}
            </Button>
            <Link
              component="button"
              type="button"
              onClick={onForgotPasswordClick}
              variant="body2"
              sx={{ alignSelf: 'center' }}
            >
              {t('forms:login.buttons.forgotPassword')}
            </Link>
          </Box>
        </Card>
      </Box>
    </Stack>
  );
};
